import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { RootState } from 'store'
import { Pagination } from 'antd'

import api from 'api'
import {
  useGetHotelSearchListResource,
  useGetRecentHotelSearchListResource,
} from 'api/hotels/getHotelSearchListResource'
import { useGetGdsCredentialsListResource } from 'api/gdsCredentials/getGdsCredentialsListResource'
import { SABRE_KEY, TRAVEL_PORT_KEY, SABRE_ID, TRAVEL_PORT_ID } from 'constants/gds'
import { validateGdsCredentials } from 'helpers/gds'
import HotelSearchListItem from 'modules/HotelsModule/types/HotelSearchListItem'
import { UserStatus } from 'modules/UsersModule/types'
import { Params, List } from './components'
import { HotelSearchParams } from './components/Params/Params'
import { NO_DATA_AVAILABLE_MESSAGE, NO_RECENT_SEARCHES } from './consts'
import GdsSelectContext from 'components/GdsSelect/GdsSelectContext'

const HotelSearch = () => {
  const { setValue: selectGds, value: selectedGds } = useContext(GdsSelectContext)
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<HotelSearchParams>({})
  const location = useLocation()
  const isAdmin = location.pathname.includes('admin')
  const history = useHistory()
  const { profile } = useSelector((state: RootState) => state.auth)
  const { data, isFetching, error } = useGetHotelSearchListResource({
    query: {
      ...filters,
      page,
      length: 20,
    },
    queryOptions: {
      enabled: Object.values(filters).length !== 0,
    },
  })
  const { data: gdsCredentialsData } = useGetGdsCredentialsListResource({
    queryOptions: {
      enabled: !isAdmin,
    },
  })

  const { data: recentHotels } = useGetRecentHotelSearchListResource({})

  useEffect(() => {
    if (profile?.status === UserStatus.Unconfirmed) {
      history.push('/account-activation')
    }
  }, [profile?.status, history.push])

  useEffect(() => {
    // to check 402 (payment required)
    api.get('/hotels-search?length=1')
  }, [])

  const isSabreAvailable = useMemo(
    () =>
      isAdmin ||
      (!!profile?.gds.some((gds) => gds.id === SABRE_ID) &&
        validateGdsCredentials(gdsCredentialsData?.data, SABRE_KEY)),
    [gdsCredentialsData, isAdmin],
  )
  const isTravelPortAvailable = useMemo(
    () =>
      isAdmin ||
      (!!profile?.gds.some((gds) => gds.id === TRAVEL_PORT_ID) &&
        validateGdsCredentials(gdsCredentialsData?.data, TRAVEL_PORT_KEY)),
    [gdsCredentialsData, isAdmin],
  )

  const onSelectHotel = useCallback((hotel: HotelSearchListItem, redirectUrl: string) => {
    if (redirectUrl.includes('travelport_id=')) {
      selectGds(TRAVEL_PORT_KEY)
    } else if (redirectUrl.includes('sabre_id=')) {
      selectGds(SABRE_KEY)
    } else if (selectedGds === SABRE_KEY && !hotel.property_ids.sabre_id && isTravelPortAvailable) {
      selectGds(TRAVEL_PORT_KEY)
    } else if (selectedGds === TRAVEL_PORT_KEY && !hotel.property_ids.travelport_id && isSabreAvailable) {
      selectGds(SABRE_KEY)
    }

    history.push(redirectUrl)
  }, [history, isSabreAvailable, isTravelPortAvailable, selectGds, selectedGds])

  const onFiltersChange = useCallback((filters) => {
    setPage(1)
    setFilters(filters)
  }, [])

  return (
    <div className="flex flex-col justify-between gap-4 bg-bg-light">
      <Params onFiltersChange={onFiltersChange} errors={error} isLoading={isFetching} />

      {Object.values(filters).length !== 0 && (
        <div className="mx-2 p-2 bg-white border border-dashed border-indigo-300 rounded-lg md:p-6 md:mx-7 shadow-xl relative">
          {isFetching && (
            <div className="absolute top-0 left-0 right-0 bottom-0 bg-gray-50 opacity-35">
              <span className="absolute top-1/2 left-1/2 w-12 h-12 inline-flex items-center justify-center -mt-6 -ml-6">
                <svg
                  aria-hidden="true"
                  className="w-10 h-10 text-gray-200 animate-spin dark:text-gray-600 fill-blue-800"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
              </span>
            </div>
          )}

          <List
            hotels={data?.data.map((hotelItem) => ({ ...hotelItem, ...hotelItem.property_ids })) || []}
            isSabreAvailable={isSabreAvailable}
            isTravelPortAvailable={isTravelPortAvailable}
            onSelectHotel={onSelectHotel}
            noDataMessage={isFetching ? '' : NO_DATA_AVAILABLE_MESSAGE}
          />

          <div className="flex items-center justify-center pt-5">
            <Pagination
              current={data?.meta.current_page || 1}
              total={data?.meta.total || 1}
              onChange={setPage}
              showSizeChanger={false}
              hideOnSinglePage
              pageSize={data?.meta.per_page || 10}
            />
          </div>
        </div>
      )}

      {(recentHotels?.data || []).length > 0 && (
        <div className="mx-2 p-2 bg-white border border-indigo-100 rounded-lg md:p-6 md:mx-7">
          <h3 className="text-xl leading-7 font-medium text-gray-800 mb-2 md:text-2xl md:leading-8 md:mb-4">
            Recent Searches
          </h3>
          <List
            hotels={recentHotels?.data || []}
            isSabreAvailable={isSabreAvailable}
            isTravelPortAvailable={isTravelPortAvailable}
            noDataMessage={NO_RECENT_SEARCHES}
            onSelectHotel={onSelectHotel}
          />
        </div>
      )}
    </div>
  )
}

export default HotelSearch
